import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  HandCoins,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  PoundSterling,
  User,
  FileText,
  Activity,
  MessageSquare,
  Edit,
  Save,
  X,
  TrendingUp,
  History,
  ArrowRightLeft,
  UserPlus,
  ArrowLeft,
  Plus,
  Minus,
  Search,
  FileSpreadsheet,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { LogCallModal, SendEmailModal } from '@/components/collections/action-modals';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import type { PromiseToPay, PromiseInvoice, PromiseAuditLog } from '@/types/collections';

import { TransactionTypeBadge } from '@/components/transactions/transaction-type-badge';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import type { Transaction, TransactionType, ColumnVisibility } from '@/types/transaction';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';
import { PromiseDetailsSidebar } from '@/components/collections/promise-details-sidebar';
import { usePromiseRecentActivities, type PromiseUnifiedActivity } from '@/hooks/usePromiseRecentActivities';
import { usePromiseById, useUpdatePromiseToPay, mapConfirmationMethodToId } from '@/hooks/usePromisesToPayData';
import { useAddPromiseNote } from '@/hooks/useAddPromiseNote';
import { useUsersList } from '@/hooks/useUsersData';
import { usePromiseAuditLogs } from '@/hooks/usePromiseAuditLogs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface EditablePromiseData {
  promiseDate: Date;
  promisedAmount: number;
  status: PromiseToPay['status'];
  confirmationMethod: PromiseToPay['confirmationMethod'];
  assignedUserId: number;
  notes: string;
}


const DEFAULT_PROMISE_DETAIL_TX_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'transactionType', label: 'Type', visible: true, order: 3 },
  { id: 'amount', label: 'Amount', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: false, order: 5 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },
  { id: 'promisedAmount', label: 'Promised Amount', visible: true, order: 7, locked: true },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'description', label: 'Description', visible: false, order: 9 },
  { id: 'accountCode', label: 'Account', visible: false, order: 10 },
  { id: 'netAmount', label: 'Net', visible: false, order: 11 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 12 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 13 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 14 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 15 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 16 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 17 },
  { id: 'onDispute', label: 'On Dispute', visible: false, order: 18 },
  { id: 'actions', label: 'Actions', visible: false, order: 19 },
];

const PromiseDetailsPage: React.FC = () => {
  const { promiseId } = useParams<{ promiseId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<EditablePromiseData | null>(null);
  const [newNote, setNewNote] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['Activity', 'Note', 'Communication']);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionDrawerOpen, setTransactionDrawerOpen] = useState(false);
  const [logCallModalOpen, setLogCallModalOpen] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Track which invoices are promised (all invoices are promised by default)
  const [promisedInvoiceIds, setPromisedInvoiceIds] = useState<Set<string>>(new Set());
  
  // State for tracking promised amounts for each invoice
  const [promisedAmounts, setPromisedAmounts] = useState<Map<string, number>>(new Map());

  const { data: activities = [], isLoading: activitiesLoading } = usePromiseRecentActivities(promiseId);
  const { data: users = [] } = useUsersList();
  const activeUsers = users.filter(user => user.status === 'active');
  const updatePromiseMutation = useUpdatePromiseToPay();
  const addNoteMutation = useAddPromiseNote();
  
  const {
    columns: promiseDetailCols,
    savePreferences: savePromiseDetailPrefs,
    resetToDefaults: resetPromiseDetailPrefs,
  } = useScreenColumnPreferences('promise-details-transactions', DEFAULT_PROMISE_DETAIL_TX_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    promiseDetailCols.forEach((col) => { vis[col.id as keyof ColumnVisibility] = col.visible; });
    return vis;
  }, [promiseDetailCols]);
  
  // Sorting state for transactions table
  type SortColumn = 'referenceNumber' | 'transactionDate' | 'dueDate' | 'transactionType' | 'amount' | 'outstandingAmount' | 'promisedAmount' | 'status' | 'currency' | 'description' | 'accountCode' | 'netAmount' | 'vatAmount' | 'subLedger' | 'orderNumber' | 'createdBy' | 'modifiedBy' | 'customerName' | 'onDispute' | null;
  type SortDirection = 'asc' | 'desc';
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Fetch the current promise directly by ID (supports navigation from case details hyperlink)
  const { data: promise = null, isLoading: isPromiseLoading } = usePromiseById(promiseId);

  // Sort invoices based on sort column and direction
  const sortedInvoices = useMemo(() => {
    if (!promise || !sortColumn) return promise?.invoices || [];

    return [...promise.invoices].sort((a, b) => {
      let aValue: string | number | null | undefined;
      let bValue: string | number | null | undefined;

      switch (sortColumn) {
        case 'referenceNumber':
          aValue = a.headerReference;
          bValue = b.headerReference;
          break;
        case 'transactionDate':
          aValue = a.invoiceDate.getTime();
          bValue = b.invoiceDate.getTime();
          break;
        case 'dueDate':
          // Calculate due date (invoice date + 30 days)
          aValue = new Date(a.invoiceDate.getTime() + 30 * 24 * 60 * 60 * 1000).getTime();
          bValue = new Date(b.invoiceDate.getTime() + 30 * 24 * 60 * 60 * 1000).getTime();
          break;
        case 'transactionType':
          aValue = 'invoice'; // All are invoices in this context
          bValue = 'invoice';
          break;
        case 'amount':
          aValue = a.originalAmount;
          bValue = b.originalAmount;
          break;
        case 'outstandingAmount':
          aValue = a.balanceRemaining;
          bValue = b.balanceRemaining;
          break;
        case 'promisedAmount':
          aValue = promisedAmounts.get(a.id) ?? a.balanceRemaining;
          bValue = promisedAmounts.get(b.id) ?? b.balanceRemaining;
          break;
        case 'status':
          aValue = a.currentStatus;
          bValue = b.currentStatus;
          break;
        case 'currency':
          aValue = a.currency ?? '';
          bValue = b.currency ?? '';
          break;
        case 'description':
          aValue = a.description ?? '';
          bValue = b.description ?? '';
          break;
        case 'accountCode':
          aValue = a.accountCode ?? '';
          bValue = b.accountCode ?? '';
          break;
        case 'netAmount':
          aValue = a.netAmount ?? 0;
          bValue = b.netAmount ?? 0;
          break;
        case 'vatAmount':
          aValue = a.vatAmount ?? 0;
          bValue = b.vatAmount ?? 0;
          break;
        case 'subLedger':
          aValue = a.subLedger ?? '';
          bValue = b.subLedger ?? '';
          break;
        case 'orderNumber':
          aValue = a.orderNumber ?? '';
          bValue = b.orderNumber ?? '';
          break;
        case 'createdBy':
          aValue = a.createdBy ?? '';
          bValue = b.createdBy ?? '';
          break;
        case 'modifiedBy':
          aValue = a.modifiedBy ?? '';
          bValue = b.modifiedBy ?? '';
          break;
        case 'customerName':
          aValue = a.customerName ?? '';
          bValue = b.customerName ?? '';
          break;
        case 'onDispute':
          aValue = a.onDispute ? 1 : 0;
          bValue = b.onDispute ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [promise, sortColumn, sortDirection, promisedAmounts]);

  // Calculate total promised amount from checked transactions
  const calculatedPromisedAmount = useMemo(() => {
    let total = 0;
    promisedInvoiceIds.forEach(invoiceId => {
      const amount = promisedAmounts.get(invoiceId);
      if (amount !== undefined) {
        total += amount;
      }
    });
    return total;
  }, [promisedInvoiceIds, promisedAmounts]);

  // Initialize promised invoices when promise data loads (all invoices are promised by default)
  useEffect(() => {
    if (promise?.invoices) {
      setPromisedInvoiceIds(new Set(promise.invoices.map(inv => inv.id)));
      // Initialize promised amounts to outstanding amounts by default
      const amounts = new Map<string, number>();
      promise.invoices.forEach(inv => {
        amounts.set(inv.id, inv.balanceRemaining);
      });
      setPromisedAmounts(amounts);
    }
  }, [promise?.invoices]);

  // Handle toggling invoice promise status
  const handleToggleInvoicePromise = (invoiceId: string) => {
    if (!isEditMode) return; // Only allow changes in edit mode
    
    setPromisedInvoiceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
        toast.success('Transaction removed from promise');
      } else {
        newSet.add(invoiceId);
        toast.success('Transaction added to promise');
      }
      return newSet;
    });
  };

  // Handler to update promised amount for an invoice
  const handlePromisedAmountChange = (invoiceId: string, value: string) => {
    // Remove commas and any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Allow empty string or valid number format
    if (cleanValue === '' || cleanValue === '.') {
      setPromisedAmounts(prev => {
        const newMap = new Map(prev);
        newMap.set(invoiceId, 0);
        return newMap;
      });
      return;
    }
    
    const numValue = parseFloat(cleanValue);
    if (!isNaN(numValue)) {
      // Find the invoice to validate against outstanding amount
      const invoice = promise?.invoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        const outstanding = invoice.balanceRemaining;

        // Validate sign consistency
        if (outstanding !== 0 && numValue !== 0 && Math.sign(numValue) !== Math.sign(outstanding)) {
          toast.error(`Amount sign must match the outstanding amount sign (${outstanding >= 0 ? 'positive' : 'negative'})`);
          return;
        }

        // Validate per-invoice amount does not exceed outstanding
        if (Math.abs(numValue) > Math.abs(outstanding)) {
          toast.error(`Amount cannot exceed outstanding amount of ${formatCurrency(outstanding)}`);
          return;
        }
      }

      setPromisedAmounts(prev => {
        const newMap = new Map(prev);
        newMap.set(invoiceId, numValue);
        return newMap;
      });
    }
  };

  const formatInputValue = (value: number): string => {
    // Format with 2 decimal places and commas
    return value.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Mock audit log data for demo mode
  const mockAuditLogs: PromiseAuditLog[] = promise
    ? [
        {
          id: 'audit-1',
          promiseId: promise.id,
          action: 'status_change',
          description: 'Promise status changed',
          fieldName: 'status',
          oldValue: 'Active',
          newValue: promise.status,
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-2',
          promiseId: promise.id,
          action: 'amount_change',
          description: 'Promised amount updated',
          fieldName: 'promisedAmount',
          oldValue: promise.promisedAmount - 500,
          newValue: promise.promisedAmount,
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-3',
          promiseId: promise.id,
          action: 'date_change',
          description: 'Promise date updated',
          fieldName: 'promiseDate',
          oldValue: format(new Date(promise.promiseDate.getTime() - 7 * 24 * 60 * 60 * 1000), 'MMM dd, yyyy'),
          newValue: format(promise.promiseDate, 'MMM dd, yyyy'),
          createdBy: 'Mike Chen',
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-4',
          promiseId: promise.id,
          action: 'collection_reassignment',
          description: 'Promise reassigned to different collection case',
          oldCaseId: 'AR-00042',
          newCaseId: promise.caseId,
          oldCaseNumber: 'AR-00042',
          newCaseNumber: promise.caseNumber,
          createdBy: 'System',
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-5',
          promiseId: promise.id,
          action: 'field_update',
          description: 'Confirmation method updated',
          fieldName: 'confirmationMethod',
          oldValue: 'Email',
          newValue: promise.confirmationMethod,
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-6',
          promiseId: promise.id,
          action: 'note_added',
          description: 'Note added to promise',
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-7',
          promiseId: promise.id,
          action: 'promise_created',
          description: 'Promise to pay created',
          createdBy: promise.createdBy,
          createdAt: promise.createdAt,
        },
      ]
    : [];

  const { data: auditLogs = [], isLoading: auditLogsLoading } = usePromiseAuditLogs(promise?.id, mockAuditLogs);

  // Filter activities based on selected tags
  const filteredActivities = selectedFilters.length === 0
    ? activities
    : activities.filter((activity) => selectedFilters.includes(activity.tag));

  // Initialize edit data when entering edit mode
  const handleEnterEditMode = () => {
    if (!promise) return;
    setEditedData({
      promiseDate: promise.promiseDate,
      promisedAmount: promise.promisedAmount,
      status: promise.status,
      confirmationMethod: promise.confirmationMethod,
      assignedUserId: promise.assignedUserId,
      notes: promise.notes,
    });
    setIsEditMode(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
  };

  // Save changes
  const handleSaveChanges = () => {
    if (!editedData || !promise) return;

    // Validation
    if (calculatedPromisedAmount <= 0) {
      toast.error('Promised amount must be greater than 0');
      return;
    }

    if (!editedData.promiseDate) {
      toast.error('Promise date is required');
      return;
    }

    // Validate per-invoice amounts and sign consistency
    for (const invoiceId of Array.from(promisedInvoiceIds)) {
      const invoice = promise.invoices.find(inv => inv.id === invoiceId);
      if (!invoice) continue;

      const allocated = promisedAmounts.get(invoiceId) ?? 0;
      const outstanding = invoice.balanceRemaining;

      if (outstanding !== 0 && allocated !== 0 && Math.sign(allocated) !== Math.sign(outstanding)) {
        toast.error(`Allocation for ${invoice.headerReference} has incorrect sign — must match the outstanding amount sign`);
        return;
      }

      if (Math.abs(allocated) > Math.abs(outstanding)) {
        toast.error(`Allocation for ${invoice.headerReference} exceeds outstanding amount of ${formatCurrency(outstanding)}`);
        return;
      }
    }

    // Validate total promised amount does not exceed total outstanding
    const totalOutstanding = promise.invoices
      .filter(inv => promisedInvoiceIds.has(inv.id))
      .reduce((sum, inv) => sum + inv.balanceRemaining, 0);

    if (Math.abs(calculatedPromisedAmount) > Math.abs(totalOutstanding)) {
      toast.error(`Total promised amount (${formatCurrency(calculatedPromisedAmount)}) exceeds total outstanding (${formatCurrency(totalOutstanding)})`);
      return;
    }

    updatePromiseMutation.mutate(
      {
        promiseId: promise.id,
        promiseDate: format(editedData.promiseDate, 'yyyy-MM-dd'),
        promisedAmount: calculatedPromisedAmount,
        confirmationMethodId: mapConfirmationMethodToId(editedData.confirmationMethod),
        assignedUserId: editedData.assignedUserId,
        notes: editedData.notes,
        status: editedData.status,
      },
      {
        onSuccess: () => {
          toast.success('Promise details updated successfully');
          setIsEditMode(false);
          setEditedData(null);
        },
        onError: (error) => {
          toast.error(`Failed to update promise: ${error.message}`);
        },
      }
    );
  };

  // Add new note
  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    if (!promiseId) return;

    addNoteMutation.mutate(
      { promiseId, notes: newNote.trim() },
      {
        onSuccess: () => {
          toast.success('Note added successfully');
          setNewNote('');
        },
        onError: (error) => {
          toast.error(`Failed to add note: ${error.message}`);
        },
      }
    );
  };



  // Handle transaction click - convert PromiseInvoice to Transaction
  const handleTransactionClick = (invoice: PromiseInvoice) => {
    if (!promise) return;
    
    // Convert PromiseInvoice to Transaction format
    const transaction: Transaction = {
      id: parseInt(invoice.id),
      referenceNumber: invoice.headerReference,
      transactionDate: invoice.transactionDate?.toISOString() ?? invoice.invoiceDate.toISOString(),
      customerId: parseInt(promise.customerId),
      customerName: invoice.customerName ?? promise.customerName,
      transactionType: (invoice.transactionType as Transaction['transactionType']) ?? 'invoice',
      amount: invoice.originalAmount,
      currency: invoice.currency ?? 'GBP',
      status: invoice.currentStatus === 'Unpaid' ? 'overdue' : invoice.currentStatus === 'Paid' ? 'completed' : 'outstanding',
      description: invoice.description ?? '',
      createdBy: invoice.createdBy ?? '',
      createdAt: invoice.transactionDate?.toISOString() ?? invoice.invoiceDate.toISOString(),
      dueDate: invoice.dueDate?.toISOString(),
      headerReference: invoice.headerReference,
      outstandingAmount: invoice.outstandingAmount ?? invoice.balanceRemaining,
      accountCode: invoice.accountCode ?? undefined,
      netAmount: invoice.netAmount ?? undefined,
      vatAmount: invoice.vatAmount ?? undefined,
      onDispute: invoice.onDispute ?? undefined,
      subLedger: invoice.subLedger ?? undefined,
      orderNumber: invoice.orderNumber ?? undefined,
      modifiedBy: invoice.modifiedBy ?? undefined,
    };
    
    setSelectedTransaction(transaction);
    setTransactionDrawerOpen(true);
  };

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    savePromiseDetailPrefs(columns);
  };

  // Export transactions to CSV
  const handleExportTransactions = (exportAll: boolean) => {
    try {
      if (!promise || sortedInvoices.length === 0) {
        toast.error('No transactions to export');
        return;
      }

      // Define all possible columns for transaction export
      const allExportColumns = [
        { key: 'referenceNumber', label: 'Reference Number' },
        { key: 'transactionDate', label: 'Transaction Date' },
        { key: 'dueDate', label: 'Due Date' },
        { key: 'transactionType', label: 'Type' },
        { key: 'amount', label: 'Gross' },
        { key: 'currency', label: 'Currency' },
        { key: 'outstandingAmount', label: 'Outstanding' },
        { key: 'promisedAmount', label: 'Promised Amount' },
        { key: 'status', label: 'Status' },
        { key: 'description', label: 'Description' },
        { key: 'accountCode', label: 'Account' },
        { key: 'netAmount', label: 'Net' },
        { key: 'vatAmount', label: 'Tax' },
        { key: 'subLedger', label: 'Sub Ledger' },
        { key: 'orderNumber', label: 'Order Number' },
        { key: 'createdBy', label: 'Created By' },
        { key: 'modifiedBy', label: 'Modified By' },
        { key: 'customerName', label: 'Customer Name' },
        { key: 'onDispute', label: 'On Dispute' },
      ];

      // Filter to visible columns if not exporting all
      const columnsToExport = exportAll
        ? allExportColumns
        : allExportColumns.filter(col => columnVisibility[col.key as keyof typeof columnVisibility]);

      const header = columnsToExport.map(col => `"${col.label}"`).join(',');

      const rows = sortedInvoices.map(invoice => {
        const isCompleted = invoice.balanceRemaining === 0;
        const isOverdue = invoice.dueDate ? invoice.dueDate < new Date() : false;
        let statusText = 'Outstanding';
        if (isCompleted) {
          statusText = 'Completed';
        } else if (isOverdue) {
          statusText = 'Overdue';
        }

        const valueMap: Record<string, string | number> = {
          referenceNumber: invoice.headerReference,
          transactionDate: invoice.transactionDate ? format(invoice.transactionDate, 'dd/MM/yyyy') : format(invoice.invoiceDate, 'dd/MM/yyyy'),
          dueDate: invoice.dueDate ? format(invoice.dueDate, 'dd/MM/yyyy') : '',
          transactionType: invoice.transactionType ?? '',
          amount: invoice.originalAmount,
          currency: invoice.currency ?? '',
          outstandingAmount: invoice.outstandingAmount ?? invoice.balanceRemaining,
          promisedAmount: promisedAmounts.get(invoice.id) ?? invoice.balanceRemaining,
          status: statusText,
          description: invoice.description ?? '',
          accountCode: invoice.accountCode ?? '',
          netAmount: invoice.netAmount ?? '',
          vatAmount: invoice.vatAmount ?? '',
          subLedger: invoice.subLedger ?? '',
          orderNumber: invoice.orderNumber ?? '',
          createdBy: invoice.createdBy ?? '',
          modifiedBy: invoice.modifiedBy ?? '',
          customerName: invoice.customerName ?? promise.customerName,
          onDispute: invoice.onDispute != null ? (invoice.onDispute ? 'Yes' : 'No') : '',
        };

        return columnsToExport.map(col => {
          const value = valueMap[col.key] ?? '';
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',');
      });

      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `promise-transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${sortedInvoices.length} transaction(s) to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export transactions');
    }
  };

  // Handle sorting for transactions table
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  // Get status badge
  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { className: string; icon: React.ReactNode }> = {
      Active: {
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      Broken: {
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        icon: <AlertCircle className="h-3 w-3" />,
      },
      Paid: {
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      'Partially Paid': {
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
        icon: <Clock className="h-3 w-3" />,
      },
      Closed: {
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
        icon: <X className="h-3 w-3" />,
      },
    };
    return (
      <Badge className={`${variants[status].className} flex items-center gap-1 w-fit`}>
        {variants[status].icon}
        {status}
      </Badge>
    );
  };

  // Get confirmation method badge
  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3" />,
      Email: <Mail className="h-3 w-3" />,
      Portal: <TrendingUp className="h-3 w-3" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1 w-fit">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  // Get activity icon based on type
  const getActivityIcon = (type: PromiseUnifiedActivity['type']) => {
    const icons: Record<PromiseUnifiedActivity['type'], React.ReactNode> = {
      email: <Mail className="h-4 w-4 text-blue-600" />,
      call: <Phone className="h-4 w-4 text-green-600" />,
      sms: <MessageSquare className="h-4 w-4 text-green-600" />,
      note: <FileText className="h-4 w-4 text-purple-600" />,
      promise: <HandCoins className="h-4 w-4 text-amber-600" />,
      payment: <PoundSterling className="h-4 w-4 text-emerald-600" />,
      status_change: <Activity className="h-4 w-4 text-amber-600" />,
      system: <Activity className="h-4 w-4 text-gray-600" />,
      case_note: <FileText className="h-4 w-4 text-blue-600" />,
      dispute: <AlertCircle className="h-4 w-4 text-red-600" />,
      communication: <MessageSquare className="h-4 w-4 text-green-600" />,
      case: <FileText className="h-4 w-4 text-blue-600" />,
    };
    return icons[type];
  };

  // Get audit log icon
  const getAuditLogIcon = (action: PromiseAuditLog['action']) => {
    switch (action) {
      case 'status_change':
        return ArrowRightLeft;
      case 'amount_change':
        return PoundSterling;
      case 'date_change':
        return Calendar;
      case 'collection_reassignment':
        return UserPlus;
      case 'field_update':
        return Edit;
      case 'note_added':
        return FileText;
      case 'promise_created':
        return History;
      case 'assigned_user_updated':
        return UserPlus;
      case 'transaction_linked':
        return Plus;
      case 'transaction_unlinked':
        return Minus;
      case 'phone_call_logged':
        return Phone;
      default:
        return Activity;
    }
  };

  // Get audit log color
  const getAuditLogColor = (action: PromiseAuditLog['action']) => {
    switch (action) {
      case 'status_change':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'amount_change':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'date_change':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      case 'collection_reassignment':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'field_update':
        return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';
      case 'note_added':
        return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
      case 'promise_created':
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
      case 'assigned_user_updated':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'transaction_linked':
        return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';
      case 'transaction_unlinked':
        return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
      case 'phone_call_logged':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  // Get tag color for unified activities
  const getTagColor = (tag: PromiseUnifiedActivity['tag']) => {
    switch (tag) {
      case 'Activity':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'Note':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800';
      case 'Communication':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
  };

  // Get icon background color for unified activities
  const getIconBackgroundColor = (tag: PromiseUnifiedActivity['tag']) => {
    switch (tag) {
      case 'Activity':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'Note':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'Communication':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedFilters(['Activity', 'Note', 'Communication']);
  };

  const activeFilterCount = 3 - selectedFilters.length;

  if (isPromiseLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!promise) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Promise Not Found</h2>
          <p className="text-muted-foreground mb-4">The promise you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/collections/promises')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Promises
          </Button>
        </div>
      </div>
    );
  }

  const displayData = isEditMode && editedData ? editedData : promise;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar */}
      <div className={cn(
        "relative transition-all duration-300 ease-in-out shrink-0",
        isSidebarCollapsed ? "w-0" : "w-80"
      )}>
        <PromiseDetailsSidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Vertical Tab/Handle - Only visible when sidebar is collapsed */}
        {isSidebarCollapsed && (
          <button
            onClick={() => setIsSidebarCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 group"
            title="Show Promises"
          >
            <div className="flex items-center bg-teal-500 hover:bg-teal-600 text-white shadow-lg rounded-r-lg transition-all duration-200 group-hover:pr-2">
              <div className="flex flex-col items-center justify-center py-4 px-2 gap-1">
                <ChevronRight className="h-5 w-5" />
                <div className="writing-mode-vertical text-xs font-medium tracking-wider">
                  PROMISES
                </div>
              </div>
            </div>
          </button>
        )}
        
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/collections/promises')}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="h-6 w-px bg-border" />
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                  <HandCoins className="h-6 w-6 text-primary" />
                  Promise Details
                </h1>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-muted-foreground">
                  Payment commitment from {promise.customerName}
                </p>
                {/* Promise ID Badge */}
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                  <span className="text-sm text-muted-foreground">Promise ID:</span>
                  <span className="text-sm font-semibold text-primary">{promise.id}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveChanges}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={handleEnterEditMode}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>

              {/* Edit Mode Indicator */}
              {isEditMode && (
                <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
                  <Edit className="h-4 w-4" />
                  <span className="font-medium">Edit Mode Active</span>
                  <span className="text-muted-foreground">- Make changes and click Save to update</span>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 pt-4 border-b shrink-0">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="activity" className="flex items-center gap-2">
                  Activity
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                    {activities.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="audit">Audit Log</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* Overview Tab */}
                <TabsContent value="overview" className="px-6 py-4 space-y-6 min-w-0 max-w-full">
              {/* Two-Column Layout: Customer Information (Left) and Promise Summary (Right) */}
              <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
                {/* Customer Information Panel - LEFT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Contact details and account information</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Two-column grid layout */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      {/* Customer - Left column (combines Account Number and Customer Name) */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer</p>
                        <p className="text-sm font-semibold">
                          [{promise.customerAccountNumber}] {promise.customerName}
                        </p>
                      </div>

                      {/* Contact Name - Right column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Contact Name</p>
                        <p className="text-sm font-semibold">{promise.contactName || '-'}</p>
                      </div>

                      {/* Email - Left column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email Address
                        </Label>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            className="h-7 w-7 p-0 bg-teal-600 hover:bg-teal-700 text-white"
                            onClick={() => setSendEmailModalOpen(true)}
                            title="Send Email"
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </Button>
                          <a
                            href={`mailto:${promise.customerEmail}`}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline block truncate"
                            title={promise.customerEmail}
                          >
                            {promise.customerEmail}
                          </a>
                        </div>
                      </div>

                      {/* Phone - Right column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Phone Number
                        </Label>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            className="h-7 w-7 p-0 bg-teal-600 hover:bg-teal-700 text-white"
                            onClick={() => setLogCallModalOpen(true)}
                            title="Log Call"
                          >
                            <Phone className="h-3.5 w-3.5" />
                          </Button>
                          <a
                            href={`tel:${promise.customerPhone}`}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {promise.customerPhone}
                          </a>
                        </div>
                      </div>

                    </div>
                  </CardContent>
                </Card>

                {/* Promise Summary Panel - RIGHT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Promise Summary
                    </CardTitle>
                    <CardDescription>Key details about this payment commitment</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Three-column grid layout for first row */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-x-4">
                        {/* Promise Date - First column */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Promise Date
                          </Label>
                          {isEditMode ? (
                            <Input
                              type="date"
                              value={format(editedData!.promiseDate, 'yyyy-MM-dd')}
                              onChange={(e) =>
                                setEditedData({ ...editedData!, promiseDate: new Date(e.target.value) })
                              }
                              className="h-8"
                            />
                          ) : (
                            <p className="text-sm font-semibold">{format(displayData.promiseDate, 'MMM dd, yyyy')}</p>
                          )}
                        </div>

                        {/* Promised Amount - Second column */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <PoundSterling className="h-3 w-3" />
                            Promised Amount
                          </Label>
                          <p className="text-sm font-bold">{formatCurrency(calculatedPromisedAmount)}</p>
                        </div>

                        {/* Assigned User - Third column */}
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <UserPlus className="h-3 w-3" />
                            Assigned User
                          </Label>
                          {isEditMode ? (
                            <Select
                              value={String(editedData!.assignedUserId)}
                              onValueChange={(value) =>
                                setEditedData({ ...editedData!, assignedUserId: parseInt(value, 10) })
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select user..." />
                              </SelectTrigger>
                              <SelectContent>
                                {activeUsers.map((user) => (
                                  <SelectItem key={user.id} value={user.id}>
                                    <div className="flex items-center gap-2">
                                      <Avatar className="h-5 w-5">
                                        <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                        <AvatarFallback className="text-[10px]">
                                          {user.firstName[0]}{user.lastName[0]}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span>{user.firstName} {user.lastName}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-sm font-semibold">{promise.assignedUserName || 'Unassigned'}</p>
                          )}
                        </div>
                      </div>

                      {/* Three-column grid layout for remaining fields */}
                      <div className="grid grid-cols-3 gap-x-4 gap-y-3">

                      {/* Status - First column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          Status
                        </Label>
                        {isEditMode ? (
                          <Select
                            value={editedData!.status}
                            onValueChange={(value) =>
                              setEditedData({ ...editedData!, status: value as PromiseToPay['status'] })
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Broken">Broken</SelectItem>
                              <SelectItem value="Paid">Paid</SelectItem>
                              <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                              <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div>{getStatusBadge(displayData.status)}</div>
                        )}
                      </div>

                      {/* Confirmation Method - Second column (aligned under Promised Amount) */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Confirmation Method
                        </Label>
                        {isEditMode ? (
                          <Select
                            value={editedData!.confirmationMethod}
                            onValueChange={(value) =>
                              setEditedData({
                                ...editedData!,
                                confirmationMethod: value as PromiseToPay['confirmationMethod'],
                              })
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Phone">Phone</SelectItem>
                              <SelectItem value="Email">Email</SelectItem>
                              <SelectItem value="Portal">Portal</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div>{getConfirmationMethodBadge(displayData.confirmationMethod)}</div>
                        )}
                      </div>

                      {/* Empty third column for alignment */}
                      <div></div>

                      {/* Created By - First column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          Created By
                        </p>
                        <p className="text-sm font-semibold">{promise.createdBy}</p>
                      </div>

                      {/* Created At - Second column (aligned under Promised Amount) */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Created At
                        </p>
                        <p className="text-sm font-semibold">{format(promise.createdAt, 'MMM dd, yyyy HH:mm')}</p>
                      </div>

                      {/* Empty third column for alignment */}
                      <div></div>

                      {/* Days Overdue - Spans 3 columns if present */}
                      {promise.daysOverdue && promise.daysOverdue > 0 && (
                        <div className="col-span-3 space-y-1">
                          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Days Overdue
                          </p>
                          <Badge variant="destructive" className="text-sm px-2 py-0.5 w-fit">
                            {promise.daysOverdue} days
                          </Badge>
                        </div>
                      )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Promise Notes Panel - Full Width */}
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 space-y-1">
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        Promise Notes
                      </CardTitle>
                      <CardDescription>
                        {isEditMode ? 'Edit promise notes or view full history in the Activity tab' : 'Quick view of promise notes'}
                      </CardDescription>
                    </div>
                    <div className="flex-1">
                      {isEditMode ? (
                        <Textarea
                          value={editedData!.notes}
                          onChange={(e) => setEditedData({ ...editedData!, notes: e.target.value })}
                          placeholder="Add notes about this promise..."
                          rows={3}
                          className="resize-none"
                        />
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm leading-relaxed">{displayData.notes || 'No notes available'}</p>
                          <Button variant="outline" size="sm" onClick={() => setActiveTab('activity')} className="gap-2">
                            <History className="h-4 w-4" />
                            View Full Activity History
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Transactions */}
              <Card className="min-w-0">
                <CardHeader>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <CardTitle className="text-lg">Transactions</CardTitle>
                      <Badge variant="secondary" className="ml-2">
                        {sortedInvoices.length}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="search"
                          placeholder="Quick search..."
                          className="pl-8 h-9 w-[200px]"
                        />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9"
                            title="Export to Excel"
                          >
                            <FileSpreadsheet className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleExportTransactions(false)}>
                            Export Current View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportTransactions(true)}>
                            Export All Columns
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ColumnConfigDialog
                        columns={promiseDetailCols}
                        onApply={handleColumnConfigApply}
                        onReset={resetPromiseDetailPrefs}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <span className="sr-only">Promised</span>
                        </TableHead>
                        {columnVisibility.referenceNumber && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('referenceNumber')}
                          >
                            <div className="flex items-center font-bold">
                              Reference Number
                              {renderSortIcon('referenceNumber')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.transactionDate && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('transactionDate')}
                          >
                            <div className="flex items-center font-bold">
                              Transaction Date
                              {renderSortIcon('transactionDate')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.dueDate && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('dueDate')}
                          >
                            <div className="flex items-center font-bold">
                              Due Date
                              {renderSortIcon('dueDate')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.transactionType && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('transactionType')}
                          >
                            <div className="flex items-center font-bold">
                              Type
                              {renderSortIcon('transactionType')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.amount && (
                          <TableHead
                            className="text-right cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('amount')}
                          >
                            <div className="flex items-center justify-end font-bold">
                              Gross
                              {renderSortIcon('amount')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.outstandingAmount && (
                          <TableHead
                            className="text-right cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('outstandingAmount')}
                          >
                            <div className="flex items-center justify-end font-bold">
                              Outstanding
                              {renderSortIcon('outstandingAmount')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.promisedAmount && (
                          <TableHead
                            className="text-right cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('promisedAmount')}
                          >
                            <div className="flex items-center justify-end font-bold">
                              Promised Amount
                              {renderSortIcon('promisedAmount')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.status && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('status')}
                          >
                            <div className="flex items-center font-bold">
                              Status
                              {renderSortIcon('status')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.currency && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('currency')}
                          >
                            <div className="flex items-center font-bold">
                              Currency
                              {renderSortIcon('currency')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.description && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('description')}
                          >
                            <div className="flex items-center font-bold">
                              Description
                              {renderSortIcon('description')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.accountCode && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('accountCode')}
                          >
                            <div className="flex items-center font-bold">
                              Account
                              {renderSortIcon('accountCode')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.netAmount && (
                          <TableHead
                            className="text-right cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('netAmount')}
                          >
                            <div className="flex items-center justify-end font-bold">
                              Net
                              {renderSortIcon('netAmount')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.vatAmount && (
                          <TableHead
                            className="text-right cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('vatAmount')}
                          >
                            <div className="flex items-center justify-end font-bold">
                              Tax
                              {renderSortIcon('vatAmount')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.subLedger && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('subLedger')}
                          >
                            <div className="flex items-center font-bold">
                              Sub Ledger
                              {renderSortIcon('subLedger')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.orderNumber && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('orderNumber')}
                          >
                            <div className="flex items-center font-bold">
                              Order Number
                              {renderSortIcon('orderNumber')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.createdBy && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('createdBy')}
                          >
                            <div className="flex items-center font-bold">
                              Created By
                              {renderSortIcon('createdBy')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.modifiedBy && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('modifiedBy')}
                          >
                            <div className="flex items-center font-bold">
                              Modified By
                              {renderSortIcon('modifiedBy')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.customerName && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('customerName')}
                          >
                            <div className="flex items-center font-bold">
                              Customer Name
                              {renderSortIcon('customerName')}
                            </div>
                          </TableHead>
                        )}
                        {columnVisibility.onDispute && (
                          <TableHead
                            className="cursor-pointer select-none hover:bg-muted/50"
                            onClick={() => handleSort('onDispute')}
                          >
                            <div className="flex items-center font-bold">
                              On Dispute
                              {renderSortIcon('onDispute')}
                            </div>
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedInvoices.map((invoice) => {
                        const isPromised = promisedInvoiceIds.has(invoice.id);
                        return (
                          <TableRow key={invoice.id}>
                            <TableCell>
                              <Checkbox
                                checked={isPromised}
                                onCheckedChange={() => handleToggleInvoicePromise(invoice.id)}
                                aria-label={`Toggle promise for ${invoice.headerReference}`}
                                disabled={!isEditMode}
                              />
                            </TableCell>
                            {columnVisibility.referenceNumber && (
                              <TableCell className="font-medium">
                                <button
                                  onClick={() => handleTransactionClick(invoice)}
                                  className="text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300 hover:underline transition-colors"
                                >
                                  {invoice.headerReference}
                                </button>
                              </TableCell>
                            )}
                            {columnVisibility.transactionDate && (
                              <TableCell>{format(invoice.invoiceDate, 'dd/MM/yyyy')}</TableCell>
                            )}
                            {columnVisibility.dueDate && (
                              <TableCell>
                                {invoice.dueDate ? format(invoice.dueDate, 'dd/MM/yyyy') : '—'}
                              </TableCell>
                            )}
                            {columnVisibility.transactionType && (
                              <TableCell>
                                <TransactionTypeBadge type={(invoice.transactionType as TransactionType) ?? 'invoice'} />
                              </TableCell>
                            )}
                            {columnVisibility.amount && (
                              <TableCell className="text-right">{formatCurrency(invoice.originalAmount)}</TableCell>
                            )}
                            {columnVisibility.outstandingAmount && (
                              <TableCell className="text-right font-medium">{formatCurrency(invoice.balanceRemaining)}</TableCell>
                            )}
                            {columnVisibility.promisedAmount && (
                              <TableCell className="text-right">
                                {isEditMode ? (
                                  <Input
                                    type="text"
                                    value={formatInputValue(promisedAmounts.get(invoice.id) ?? invoice.balanceRemaining)}
                                    onChange={(e) => handlePromisedAmountChange(invoice.id, e.target.value)}
                                    className="text-right h-8 w-32 ml-auto"
                                  />
                                ) : (
                                  <span className="font-medium">
                                    {formatCurrency(promisedAmounts.get(invoice.id) ?? invoice.balanceRemaining)}
                                  </span>
                                )}
                              </TableCell>
                            )}
                            {columnVisibility.status && (
                              <TableCell>
                                {(() => {
                                  const isCompleted = invoice.balanceRemaining === 0;
                                  const isOverdue = invoice.dueDate ? invoice.dueDate < new Date() : false;
                                  
                                  if (isCompleted) {
                                    return <Badge variant="secondary">Completed</Badge>;
                                  } else if (isOverdue) {
                                    return <Badge variant="destructive">Overdue</Badge>;
                                  } else {
                                    return <Badge variant="default">Outstanding</Badge>;
                                  }
                                })()}
                              </TableCell>
                            )}
                            {columnVisibility.currency && (
                              <TableCell>{invoice.currency ?? '—'}</TableCell>
                            )}
                            {columnVisibility.description && (
                              <TableCell>{invoice.description ?? '—'}</TableCell>
                            )}
                            {columnVisibility.accountCode && (
                              <TableCell>{invoice.accountCode ?? '—'}</TableCell>
                            )}
                            {columnVisibility.netAmount && (
                              <TableCell className="text-right">{invoice.netAmount != null ? formatCurrency(invoice.netAmount) : '—'}</TableCell>
                            )}
                            {columnVisibility.vatAmount && (
                              <TableCell className="text-right">{invoice.vatAmount != null ? formatCurrency(invoice.vatAmount) : '—'}</TableCell>
                            )}
                            {columnVisibility.subLedger && (
                              <TableCell>{invoice.subLedger ?? '—'}</TableCell>
                            )}
                            {columnVisibility.orderNumber && (
                              <TableCell>{invoice.orderNumber ?? '—'}</TableCell>
                            )}
                            {columnVisibility.createdBy && (
                              <TableCell>{invoice.createdBy ?? '—'}</TableCell>
                            )}
                            {columnVisibility.modifiedBy && (
                              <TableCell>{invoice.modifiedBy ?? '—'}</TableCell>
                            )}
                            {columnVisibility.customerName && (
                              <TableCell>{invoice.customerName ?? promise.customerName}</TableCell>
                            )}
                            {columnVisibility.onDispute && (
                              <TableCell>{invoice.onDispute != null ? (invoice.onDispute ? 'Yes' : 'No') : '—'}</TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Outstanding:</span>
                      <span className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(sortedInvoices.reduce((sum, inv) => sum + inv.balanceRemaining, 0))}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Promised Amount:</span>
                      <span className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(
                          Array.from(promisedInvoiceIds).reduce((sum, invoiceId) => {
                            return sum + (promisedAmounts.get(invoiceId) ?? 0);
                          }, 0)
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Activity Tab - Consolidated view of Actions and Notes */}
            <TabsContent value="activity" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">Activity Timeline</CardTitle>
                      <CardDescription>Complete history of all activities and notes</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {filteredActivities.length} {filteredActivities.length === 1 ? 'item' : 'items'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Filter Controls */}
                  <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Filter by Type
                      </Label>
                      {activeFilterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs">
                          <X className="h-3 w-3 mr-1" />
                          Clear Filters ({activeFilterCount})
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant={selectedFilters.includes('Activity') ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (selectedFilters.includes('Activity')) {
                            setSelectedFilters(selectedFilters.filter((f) => f !== 'Activity'));
                          } else {
                            setSelectedFilters([...selectedFilters, 'Activity']);
                          }
                        }}
                        className={cn(
                          'h-8',
                          selectedFilters.includes('Activity') &&
                            'bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700'
                        )}
                      >
                        <Activity className="h-4 w-4 mr-2" />
                        Activity
                      </Button>
                      <Button
                        variant={selectedFilters.includes('Note') ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (selectedFilters.includes('Note')) {
                            setSelectedFilters(selectedFilters.filter((f) => f !== 'Note'));
                          } else {
                            setSelectedFilters([...selectedFilters, 'Note']);
                          }
                        }}
                        className={cn(
                          'h-8',
                          selectedFilters.includes('Note') &&
                            'bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-600 dark:hover:bg-purple-700'
                        )}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Notes
                      </Button>
                      <Button
                        variant={selectedFilters.includes('Communication') ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (selectedFilters.includes('Communication')) {
                            setSelectedFilters(selectedFilters.filter((f) => f !== 'Communication'));
                          } else {
                            setSelectedFilters([...selectedFilters, 'Communication']);
                          }
                        }}
                        className={cn(
                          'h-8',
                          selectedFilters.includes('Communication') &&
                            'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700'
                        )}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Communications
                      </Button>
                    </div>
                  </div>

                  {/* Add New Note Section */}
                  <Separator />
                  <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
                    <Label htmlFor="newNote" className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Add New Note
                    </Label>
                    <Textarea
                      id="newNote"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Enter your note here..."
                      rows={4}
                      className="resize-none"
                    />
                    <div className="flex justify-end">
                      <Button onClick={handleAddNote} disabled={!newNote.trim()} size="sm">
                        <Save className="h-4 w-4 mr-2" />
                        Add Note
                      </Button>
                    </div>
                  </div>

                  {/* Timeline Display */}
                  {activitiesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : filteredActivities.length > 0 ? (
                    <>
                      <Separator />
                      <div className="space-y-4">
                        {filteredActivities.map((activity, index) => (
                          <div key={activity.id} className="relative">
                            {index !== filteredActivities.length - 1 && (
                              <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                            )}
                            <div className="flex gap-4">
                              <div
                                className={cn(
                                  'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                                  getIconBackgroundColor(activity.tag)
                                )}
                              >
                                {getActivityIcon(activity.type)}
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Badge variant="outline" className={cn('text-xs font-medium', getTagColor(activity.tag))}>
                                        {activity.tag}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-foreground leading-relaxed">{activity.description}</p>
                                    {activity.outcome && (
                                      <p className="text-sm text-muted-foreground italic">Outcome: {activity.outcome}</p>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                                    {format(activity.createdAt, 'MMM d, yyyy h:mm a')}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    <User className="h-3 w-3 mr-1" />
                                    {activity.createdBy}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm font-medium">No activities match your filters</p>
                      <p className="text-xs mt-1">Try adjusting your filter selection</p>
                      {activeFilterCount > 0 && (
                        <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
                          Clear All Filters
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Log Tab */}
            <TabsContent value="audit" className="px-6 py-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>Complete chronological history of all promise modifications and changes</CardDescription>
                </CardHeader>
                <CardContent>
                  {auditLogsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : auditLogs.length > 0 ? (
                  <div className="space-y-4">
                    {auditLogs.map((log, index) => {
                      const Icon = getAuditLogIcon(log.action);
                      const colorClass = getAuditLogColor(log.action);

                      return (
                        <div key={log.id} className="relative">
                          {index !== auditLogs.length - 1 && <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />}
                          <div className="flex gap-4">
                            <div className={cn('flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0', colorClass)}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-1">
                                  <p className="font-medium text-sm">{log.description}</p>

                                  {/* Status/Amount/Date/Field Changes */}
                                  {(log.action === 'status_change' ||
                                    log.action === 'amount_change' ||
                                    log.action === 'date_change' ||
                                    log.action === 'field_update' ||
                                    log.action === 'assigned_user_updated') &&
                                    log.oldValue &&
                                    log.newValue && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <Badge variant="outline" className="text-xs">
                                          {log.action === 'amount_change' && typeof log.oldValue === 'number'
                                            ? formatCurrency(log.oldValue)
                                            : log.oldValue}
                                        </Badge>
                                        <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                        <Badge variant="outline" className="text-xs">
                                          {log.action === 'amount_change' && typeof log.newValue === 'number'
                                            ? formatCurrency(log.newValue)
                                            : log.newValue}
                                        </Badge>
                                      </div>
                                    )}

                                  {/* Transaction Linked/Unlinked */}
                                  {(log.action === 'transaction_linked' || log.action === 'transaction_unlinked') && (log.oldValue || log.newValue) && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Badge variant="outline" className="text-xs font-mono">
                                        {log.action === 'transaction_linked' ? log.newValue : log.oldValue}
                                      </Badge>
                                    </div>
                                  )}

                                  {/* Collection Reassignment */}
                                  {log.action === 'collection_reassignment' && log.oldCaseNumber && log.newCaseNumber && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs">From:</span>
                                        <Badge variant="outline" className="text-xs font-mono">
                                          {log.oldCaseNumber}
                                        </Badge>
                                        <ArrowRightLeft className="h-3 w-3" />
                                        <span className="text-xs">To:</span>
                                        <Badge variant="outline" className="text-xs font-mono">
                                          {log.newCaseNumber}
                                        </Badge>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground whitespace-nowrap">
                                  {format(log.createdAt, 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  <User className="h-3 w-3 mr-1" />
                                  {log.createdBy}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No audit log entries found for this promise.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
                </TabsContent>
            </div>
          </Tabs>

          {/* Transaction Details Drawer */}
          <TransactionDetailSheet
            open={transactionDrawerOpen}
            onOpenChange={setTransactionDrawerOpen}
            transaction={selectedTransaction}
            onViewDocument={(transaction) => {
              console.log('View document for transaction:', transaction);
              // In a real app, this would open the document
            }}
          />

          {/* Log Call Modal */}
          <LogCallModal
            open={logCallModalOpen}
            onOpenChange={setLogCallModalOpen}
            customerName={promise?.customerName || ''}
            customerPhone={promise?.customerPhone}
            promiseId={promise?.id}
          />

          {/* Send Email Modal */}
          <SendEmailModal
            open={sendEmailModalOpen}
            onOpenChange={setSendEmailModalOpen}
            customerName={promise?.customerName || ''}
            customerEmail={promise?.customerEmail || ''}
            customerId={promise?.customerId}
            sourceType="promise"
            sourceId={promise?.id}
          />
        </div>
      </div>
    </div>
  );
};

export default PromiseDetailsPage;
